import { useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import {
    clientesDosBlocos, corTextoSobre, formatarNumero, formatarPercentual, formatarPercentualCurto, participacaoPorCliente, temaGrafico,
} from "./analiseUtils";
import DetalheParticipacao from "./DetalheParticipacao";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const ALTURA_BARRA = 40;
const MIN_PX_ROTULO = 34; // fatia mais estreita que isso fica sem o % (aparece ao passar o mouse)
const ALFA_APAGADO = 0.2;

const comAlfa = (hex, alfa) => {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map(i => parseInt(valor.substring(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alfa})`;
};

/** % escrito dentro de cada fatia larga o bastante (e não apagada). */
const rotuloNoSegmento = {
    id: "rotuloNoSegmento",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di);
            if (meta.hidden) return;
            meta.data.forEach((segmento, i) => {
                const valor = dataset.data[i];
                if (!valor || dataset.apagado[i]) return;
                const { x, y, base } = segmento.getProps(["x", "y", "base"], true);
                if (Math.abs(x - base) < MIN_PX_ROTULO) return;
                ctx.fillStyle = corTextoSobre(dataset.corBase);
                ctx.fillText(formatarPercentualCurto(valor), (x + base) / 2, y);
            });
        });
        ctx.restore();
    },
};

/** Fichas do cliente à direita de cada barra e, com destaque, a parte dos destacados. */
const fichasNaPonta = {
    id: "fichasNaPonta",
    afterDatasetsDraw(chart, args, opcoes) {
        const { ctx, chartArea } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta) return;
        ctx.save();
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        meta.data.forEach((segmento, i) => {
            const { y } = segmento.getProps(["y"], true);
            const doDestaque = opcoes.destaque ? opcoes.destaque[i] : null;
            ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
            ctx.fillStyle = opcoes.cor;
            ctx.fillText(`${formatarNumero(opcoes.fichas[i])} fichas`, chartArea.right + 10, doDestaque === null ? y : y - 7);
            if (doDestaque !== null) {
                ctx.font = "400 11px Roboto, Helvetica, Arial, sans-serif";
                ctx.fillStyle = opcoes.corSecundaria;
                ctx.fillText(`destaq. ${formatarPercentualCurto(doDestaque)}`, chartArea.right + 10, y + 7);
            }
        });
        ctx.restore();
    },
};

/**
 * Participação por cliente: uma barra por cliente com fichas no período (soma 100%), dividida entre todas as pessoas que
 * o atenderam, cada uma com a sua cor (sem "Outros"). O % fica dentro da fatia só quando cabe; o resto aparece ao passar o
 * mouse. "Destacar" escolhe uma ou várias pessoas e apaga as demais, que continuam na barra como referência (as do filtro
 * da tela, `selecionados`, entram no destaque; os clientes do filtro, `clientesDestaque`, ficam acesos e os outros apagados). Passar o mouse num nome da legenda ou numa fatia realça a pessoa. "Ver
 * detalhes" abre a tabela completa (cliente, pessoa, fichas e %).
 */
export default function GraficoEsforco({ blocos, cores, fichasPorCliente, selecionados = [], clientesDestaque = [], rotuloPessoa, rotuloPessoas }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const [destacados, setDestacados] = useState([]);
    const [emFoco, setEmFoco] = useState(null);
    const [detalhesAberto, setDetalhesAberto] = useState(false);
    const focoRef = useRef(null);

    const participacoes = useMemo(
        () => clientesDosBlocos(blocos).map(cliente => ({ cliente, ...participacaoPorCliente(blocos, cliente) })).filter(p => p.total > 0),
        [blocos]
    );
    // Pessoas com alguma fatia, na ordem das cores (mais atendimentos primeiro)
    const pessoas = useMemo(() => {
        const comFatia = new Set(participacoes.flatMap(p => p.partes.map(parte => parte.nome)));
        return cores.ordem.filter(nome => comFatia.has(nome));
    }, [participacoes, cores]);

    // Quem saiu dos dados (outro período ou carteira) sai do destaque
    const destacadosValidos = destacados.filter(nome => pessoas.includes(nome));
    const realce = new Set(destacadosValidos);
    selecionados.forEach(nome => realce.add(nome));
    const comFoco = new Set(realce);
    if (emFoco) comFoco.add(emFoco);
    const algumRealce = comFoco.size > 0;

    const focar = (nome) => {
        if (focoRef.current === nome) return;
        focoRef.current = nome;
        setEmFoco(nome);
    };
    const alternar = (nome) => setDestacados(atuais => (atuais.includes(nome) ? atuais.filter(n => n !== nome) : [...atuais, nome]));

    if (participacoes.length === 0) {
        return (
            <MDTypography variant="caption" color="text" sx={{ display: "block", py: 3, textAlign: "center" }}>
                Sem atendimentos no período.
            </MDTypography>
        );
    }

    const data = {
        labels: participacoes.map(p => p.cliente),
        datasets: pessoas.map(nome => {
            const cor = cores.cor(nome);
            const apagado = participacoes.map(p => (algumRealce && !comFoco.has(nome)) || (clientesDestaque.length > 0 && !clientesDestaque.includes(p.cliente)));
            return {
                label: nome,
                corBase: cor,
                apagado,
                data: participacoes.map(p => (p.partes.find(parte => parte.nome === nome) || {}).percentual || 0),
                quantidades: participacoes.map(p => (p.partes.find(parte => parte.nome === nome) || {}).quantidade || 0),
                backgroundColor: apagado.map(a => (a ? comAlfa(cor, ALFA_APAGADO) : cor)),
                borderColor: tema.superficie,
                borderWidth: { left: 1, right: 1 },
                borderSkipped: false,
                borderRadius: 3,
                barThickness: 26,
            };
        }),
    };

    const destaquePorCliente = realce.size > 0
        ? participacoes.map(p => p.partes.filter(parte => realce.has(parte.nome)).reduce((acc, parte) => acc + parte.percentual, 0))
        : null;

    const options = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 200 },
        layout: { padding: { right: 92 } },
        interaction: { mode: "nearest", intersect: true },
        onHover: (evento, elementos, chart) => {
            focar(elementos.length ? chart.data.datasets[elementos[0].datasetIndex].label : null);
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                filter: (item) => item.parsed.x > 0,
                callbacks: {
                    title: (itens) => (itens[0] ? itens[0].label : ""),
                    label: (ctx) => {
                        const quantidade = ctx.dataset.quantidades[ctx.dataIndex];
                        const total = participacoes[ctx.dataIndex].total;
                        return `${ctx.dataset.label}: ${formatarPercentual(ctx.parsed.x)} (${formatarNumero(quantidade)} de ${formatarNumero(total)} fichas acionadas mês a mês)`;
                    },
                },
            },
            fichasNaPonta: {
                cor: tema.texto,
                corSecundaria: tema.textoSecundario,
                fichas: participacoes.map(p => fichasPorCliente[p.cliente] || 0),
                destaque: destaquePorCliente,
            },
        },
        scales: {
            x: {
                stacked: true,
                min: 0,
                max: 1,
                grid: { color: tema.grade },
                border: { display: false },
                ticks: { color: tema.textoSecundario, font: { size: 11 }, stepSize: 0.2, callback: (v) => formatarPercentualCurto(v) },
            },
            y: {
                stacked: true,
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: tema.texto,
                    font: (ctx) => ({ size: 12, weight: clientesDestaque.includes(participacoes[ctx.index]?.cliente) ? "bold" : "600" }),
                },
            },
        },
    };

    const opcoesDestaque = pessoas.map(nome => ({ id: nome, value: nome, label: nome }));

    return (
        <MDBox>
            <MDBox display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                <MDBox flex={1} minWidth={0}>
                    <MDSelect
                        isMulti
                        name="destacarParticipacao"
                        placeholder={`Destacar ${rotuloPessoas}...`}
                        value={opcoesDestaque.filter(o => destacadosValidos.includes(o.value))}
                        options={opcoesDestaque}
                        onChange={vals => setDestacados((vals || []).map(v => v.value))}
                    />
                </MDBox>
                <MDButton variant="outlined" color="info" size="small" sx={{ height: 38, whiteSpace: "nowrap" }} onClick={() => setDetalhesAberto(true)}>
                    <Icon sx={{ mr: 0.5 }}>table_view</Icon> Ver detalhes
                </MDButton>
            </MDBox>

            <MDBox display="flex" flexWrap="wrap" columnGap={1.5} rowGap={0.5} mb={1} onMouseLeave={() => focar(null)}>
                {pessoas.map(nome => {
                    const ativo = realce.has(nome);
                    return (
                        <MDBox
                            key={nome}
                            component="button"
                            type="button"
                            onClick={() => alternar(nome)}
                            onMouseEnter={() => focar(nome)}
                            title={ativo ? `Tirar o destaque de ${nome}` : `Destacar ${nome}`}
                            display="inline-flex"
                            alignItems="center"
                            gap={0.75}
                            sx={{
                                border: 0, background: "transparent", p: 0, cursor: "pointer", font: "inherit",
                                fontSize: "0.72rem", lineHeight: 1.5, color: tema.texto,
                                fontWeight: ativo ? 700 : 400,
                                opacity: algumRealce && !comFoco.has(nome) ? 0.5 : 1,
                            }}
                        >
                            <MDBox component="span" sx={{ width: 9, height: 9, minWidth: 9, borderRadius: "50%", background: cores.cor(nome) }} />
                            {nome}
                        </MDBox>
                    );
                })}
            </MDBox>

            <MDBox height={`${participacoes.length * ALTURA_BARRA + 40}px`} onMouseLeave={() => focar(null)}>
                <Bar data={data} options={options} plugins={[rotuloNoSegmento, fichasNaPonta]} />
            </MDBox>

            {detalhesAberto && (
                <DetalheParticipacao
                    participacoes={participacoes}
                    pessoas={pessoas}
                    cores={cores}
                    destacados={realce}
                    rotuloPessoa={rotuloPessoa}
                    onFechar={() => setDetalhesAberto(false)}
                />
            )}
        </MDBox>
    );
}

